// ไฟล์: frontend/src/pages/teacher/Dashboard.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import api from "../../services/api";
import authService from "../../services/auth";
import Swal from "sweetalert2";
import { DEFAULT_AVATAR } from '../../assets/default_avatar';

function TeacherDashboard() {
  const [classes, setClasses] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const currentUser = authService.getCurrentUser();

  // วันที่ปัจจุบัน
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    // เรียกข้อมูลชั้นเรียนที่ครูรับผิดชอบ
    const fetchClasses = async () => {
      try {
        const response = await api.class.getMyClasses();
        const myClasses = response.data.data || [];
        setClasses(myClasses);

        // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
        if (myClasses.length > 0) {
          setSelectedClassId(myClasses[0].id);
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลชั้นเรียนได้",
          confirmButtonColor: "#ff6b01",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClasses();
  }, []);

  useEffect(() => {
    // เรียกข้อมูลการเข้าเรียนวันนี้สำหรับชั้นเรียนที่เลือก
    const fetchTodayAttendance = async () => {
      if (!selectedClassId) return;

      try {
        setIsLoading(true);
        const response = await api.attendance.getAttendanceByClassAndDate(
          selectedClassId,
          today
        );
        setTodayAttendance(response.data.data || []);
      } catch (error) {
        console.error("Error fetching today attendance:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลการเข้าเรียนวันนี้ได้",
          confirmButtonColor: "#ff6b01",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTodayAttendance();
  }, [selectedClassId, today]);

  // สรุปจำนวนสถานะการเข้าเรียน
  const getAttendanceSummary = () => {
    const summary = {
      present: 0,
      absent: 0,
      sick_leave: 0,
      personal_leave: 0,
      late: 0,
      not_recorded: 0,
    };

    todayAttendance.forEach((item) => {
      if (!item.status) {
        summary.not_recorded++;
      } else {
        summary[item.status]++;
      }
    });

    return summary;
  };

  const attendanceSummary = getAttendanceSummary();

  // เพิ่ม state สำหรับเก็บข้อมูลการอบรม
  const [trainingSummary, setTrainingSummary] = useState({
    total_hours: 0,
    completed_count: 0,
    in_progress_count: 0,
  });
  const [recentTrainings, setRecentTrainings] = useState([]);

  // เพิ่มการดึงข้อมูลการอบรมใน useEffect
  useEffect(() => {
    fetchMyTrainings();
    fetchTrainingSummary();
  }, []);

  // เพิ่มฟังก์ชันดึงข้อมูลการอบรม
  const fetchMyTrainings = async () => {
    try {
      const response = await api.teacherTraining.getMyTrainings();
      // เรียงลำดับตามวันที่เริ่มต้นล่าสุด
      const trainings = response.data.data || [];
      const sorted = [...trainings].sort(
        (a, b) => new Date(b.start_date) - new Date(a.start_date)
      );
      // เอาเฉพาะ 3 รายการล่าสุด
      setRecentTrainings(sorted.slice(0, 3));
    } catch (error) {
      console.error("Error fetching trainings:", error);
    }
  };

  const fetchTrainingSummary = async () => {
    try {
      // ดึงข้อมูลสรุปการอบรมของปีปัจจุบัน
      const currentYear = new Date().getFullYear();
      const response = await api.teacherTraining.getMyTrainingSummary(
        currentYear
      );
      setTrainingSummary(
        response.data.data || {
          total_hours: 0,
          completed_count: 0,
          in_progress_count: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching training summary:", error);
    }
  };

  // แปลงสถานะเป็นป้ายสี
  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <span className="badge badge-success">เสร็จสิ้น</span>;
      case "in_progress":
        return <span className="badge badge-info">กำลังอบรม</span>;
      case "registered":
        return <span className="badge badge-warning">ลงทะเบียน</span>;
      case "cancelled":
        return <span className="badge badge-danger">ยกเลิก</span>;
      default:
        return <span className="badge badge-secondary">{status}</span>;
    }
  };

  return (
    <div className="wrapper">
      <Header />
      <Sidebar />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">แดชบอร์ดครูประจำชั้น</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item active">แดชบอร์ด</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* แสดงข้อมูลส่วนตัว */}
                <div className="row">
                  <div className="col-md-12">
                    <div className="card card-orange card-outline">
                      <div className="card-body box-profile">
                        <div className="text-center">
                          <img
                            className="profile-user-img img-fluid img-circle"
                            src={`https://cms2568.backend.kruwirat.com/uploads/${currentUser.profile_pic}`}
                            alt="User profile"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = DEFAULT_AVATAR;
                            }}
                          />
                        </div>
                        <h3 className="profile-username text-center">
                          {currentUser.first_name} {currentUser.last_name}
                        </h3>
                        <p className="text-muted text-center">ครูประจำชั้น</p>
                        <p className="text-center">
                          <Link
                            to="/teacher/profile"
                            className="btn btn-outline-orange btn-sm"
                          >
                            <i className="fas fa-user-edit mr-1"></i>{" "}
                            แก้ไขข้อมูลส่วนตัว
                          </Link>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* แสดงชั้นเรียนที่รับผิดชอบ */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">ชั้นเรียนที่รับผิดชอบ</h3>
                      </div>
                      <div className="card-body">
                        {classes.length === 0 ? (
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ
                            กรุณาติดต่อผู้ดูแลระบบ
                          </div>
                        ) : (
                          <div className="row">
                            {classes.map((classItem) => (
                              <div
                                key={classItem.id}
                                className="col-md-4 col-sm-6 col-12"
                              >
                                <div className="info-box bg-orange">
                                  <span className="info-box-icon">
                                    <i className="fas fa-chalkboard-teacher"></i>
                                  </span>
                                  <div className="info-box-content">
                                    <span className="info-box-text">
                                      {classItem.class_name}
                                    </span>
                                    <span className="info-box-number">
                                      ห้อง {classItem.room_number}
                                    </span>
                                    <div className="progress">
                                      <div
                                        className="progress-bar"
                                        style={{ width: "100%" }}
                                      ></div>
                                    </div>
                                    <span className="progress-description">
                                      {classItem.student_count || 0} นักเรียน
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* การเข้าเรียนวันนี้ */}
                {classes.length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">
                            การเข้าเรียนวันนี้ ({today})
                          </h3>

                          <div className="card-tools">
                            <div
                              className="input-group input-group-sm"
                              style={{ width: "200px" }}
                            >
                              <select
                                className="form-control"
                                value={selectedClassId}
                                onChange={(e) =>
                                  setSelectedClassId(e.target.value)
                                }
                              >
                                {classes.map((classItem) => (
                                  <option
                                    key={classItem.id}
                                    value={classItem.id}
                                  >
                                    {classItem.class_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-12 col-sm-6 col-md-4">
                              <div className="info-box bg-success">
                                <span className="info-box-icon">
                                  <i className="fas fa-check-circle"></i>
                                </span>
                                <div className="info-box-content">
                                  <span className="info-box-text">มาเรียน</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.present} คน
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                              <div className="info-box bg-danger">
                                <span className="info-box-icon">
                                  <i className="fas fa-times-circle"></i>
                                </span>
                                <div className="info-box-content">
                                  <span className="info-box-text">
                                    ขาดเรียน
                                  </span>
                                  <span className="info-box-number">
                                    {attendanceSummary.absent} คน
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                              <div className="info-box bg-info">
                                <span className="info-box-icon">
                                  <i className="fas fa-notes-medical"></i>
                                </span>
                                <div className="info-box-content">
                                  <span className="info-box-text">ลาป่วย</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.sick_leave} คน
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                              <div className="info-box bg-secondary">
                                <span className="info-box-icon">
                                  <i className="fas fa-calendar-minus"></i>
                                </span>
                                <div className="info-box-content">
                                  <span className="info-box-text">ลากิจ</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.personal_leave} คน
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                              <div className="info-box bg-warning">
                                <span className="info-box-icon">
                                  <i className="fas fa-hourglass-half"></i>
                                </span>
                                <div className="info-box-content">
                                  <span className="info-box-text">มาสาย</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.late} คน
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                              <div className="info-box bg-light">
                                <span className="info-box-icon">
                                  <i className="fas fa-question-circle"></i>
                                </span>
                                <div className="info-box-content">
                                  <span className="info-box-text">
                                    ยังไม่บันทึก
                                  </span>
                                  <span className="info-box-number">
                                    {attendanceSummary.not_recorded} คน
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <Link
                              to="/teacher/attendance"
                              className="btn btn-orange"
                            >
                              <i className="fas fa-calendar-check mr-2"></i>
                              บันทึกการเข้าเรียน
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* // เพิ่มส่วนแสดงข้อมูลการอบรมในหน้า Dashboard
            (ใส่ต่อจากส่วนการเข้าเรียน) */}
            {/* ข้อมูลการอบรม */}
            <div className="row">
              <div className="col-12">
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">การอบรมของฉัน</h3>
                    <div className="card-tools">
                      <Link to="/teacher/trainings" className="btn btn-tool">
                        <i className="fas fa-arrow-right"></i> ดูทั้งหมด
                      </Link>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="info-box bg-info">
                          <span className="info-box-icon">
                            <i className="fas fa-clock"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">
                              จำนวนชั่วโมงอบรม
                            </span>
                            <span className="info-box-number">
                              {trainingSummary.total_hours || 0}
                            </span>
                            <span className="progress-description">
                              ชั่วโมงการอบรมในปีนี้
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="info-box bg-success">
                          <span className="info-box-icon">
                            <i className="fas fa-check-circle"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">อบรมเสร็จสิ้น</span>
                            <span className="info-box-number">
                              {trainingSummary.completed_count || 0}
                            </span>
                            <span className="progress-description">
                              หลักสูตรที่เสร็จสิ้นแล้ว
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="info-box bg-warning">
                          <span className="info-box-icon">
                            <i className="fas fa-spinner"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">กำลังอบรม</span>
                            <span className="info-box-number">
                              {trainingSummary.in_progress_count || 0}
                            </span>
                            <span className="progress-description">
                              หลักสูตรที่กำลังอบรม
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h5 className="mt-4">การอบรมล่าสุด</h5>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>หลักสูตร</th>
                            <th>วันที่</th>
                            <th>ชั่วโมง</th>
                            <th>สถานะ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentTrainings.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="text-center">
                                ไม่พบข้อมูลการอบรมล่าสุด
                              </td>
                            </tr>
                          ) : (
                            recentTrainings.map((training) => (
                              <tr key={training.id}>
                                <td>
                                  {training.course_name}
                                  <small className="d-block text-muted">
                                    {training.organizer}
                                  </small>
                                </td>
                                <td>
                                  {new Date(
                                    training.start_date
                                  ).toLocaleDateString("th-TH")}
                                  {" - "}
                                  {new Date(
                                    training.end_date
                                  ).toLocaleDateString("th-TH")}
                                </td>
                                <td>{training.hours}</td>
                                <td>{getStatusBadge(training.status)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-3">
                      <Link to="/teacher/trainings" className="btn btn-orange">
                        <i className="fas fa-certificate mr-2"></i>
                        จัดการข้อมูลการอบรม
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default TeacherDashboard;
